/**
 * Frontend HTTP client for the email API (Express email-server.js).
 * Uses env: REACT_APP_EMAIL_API_ENDPOINT (optional), ecdtportal.com / wasl.om production rules, else localhost.
 */

const WASL_PRODUCTION_ENDPOINT = 'https://wasl.om/api/email/send-email';
/** Public portal + email API when the SPA is served from ecdtportal.com (reverse-proxy /api → email-server). */
export const ECDT_PORTAL_PUBLIC_URL = 'https://ecdtportal.com';
const ECDT_PORTAL_EMAIL_ENDPOINT = `${ECDT_PORTAL_PUBLIC_URL}/api/email/send-email`;
/** Direct URL when not using CRA proxy (e.g. tests, SSR) */
const LOCAL_DIRECT = 'http://localhost:3001/api/send-email';

function isEcdtPortalHost(host) {
  return (
    host === 'ecdtportal.com' ||
    host === 'www.ecdtportal.com' ||
    (typeof host === 'string' && host.endsWith('.ecdtportal.com'))
  );
}

/**
 * Links in most transactional emails. Set REACT_APP_PORTAL_URL at build time for staging/custom domains.
 * Production builds default to https://ecdtportal.com; local dev uses the current origin.
 */
export function getPortalUrlForEmails() {
  const fromEnv = process.env.REACT_APP_PORTAL_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    return (window.location.origin || '').replace(/\/$/, '') || ECDT_PORTAL_PUBLIC_URL;
  }
  return ECDT_PORTAL_PUBLIC_URL;
}

/**
 * HR invite emails should point new users at the live portal (not localhost), unless REACT_APP_PORTAL_URL overrides.
 */
export function getPortalUrlForHrInvites() {
  const fromEnv = process.env.REACT_APP_PORTAL_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, '');
  }
  return ECDT_PORTAL_PUBLIC_URL;
}

/**
 * Full URL/path for POST send-email.
 * On localhost, relative `/api/send-email` uses package.json proxy (no CORS).
 */
export function getEmailEndpoint() {
  const fromEnv = process.env.REACT_APP_EMAIL_API_ENDPOINT;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim();
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host === 'wasl.om' || host.endsWith('.wasl.om')) {
      return WASL_PRODUCTION_ENDPOINT;
    }
    if (isEcdtPortalHost(host)) {
      return ECDT_PORTAL_EMAIL_ENDPOINT;
    }
    // CRA dev: package.json "proxy" forwards /api → email-server for ANY hostname (LAN IP, localhost).
    // Previously only localhost/127.0.0.1 used the proxy; 192.168.x.x fell through to http://localhost:3001
    // which breaks from other devices or misroutes on some setups.
    if (process.env.NODE_ENV === 'development') {
      return '/api/send-email';
    }
    if (host === 'localhost' || host === '127.0.0.1') {
      return '/api/send-email';
    }
    // Built SPA (npm run build) on any other host: use ecdtportal email API unless REACT_APP_EMAIL_API_ENDPOINT is set.
    if (process.env.NODE_ENV === 'production') {
      return ECDT_PORTAL_EMAIL_ENDPOINT;
    }
  }
  return LOCAL_DIRECT;
}

/**
 * Origin for sibling routes (/api/send-recovery). Empty string = same-origin (proxy).
 */
export function getEmailApiOrigin() {
  const ep = getEmailEndpoint();
  if (ep.startsWith('/')) return '';
  try {
    const u = new URL(ep);
    return `${u.protocol}//${u.host}`;
  } catch {
    return '';
  }
}

function validateSendOptions(options) {
  const { to, subject, body, html, attachments } = options || {};
  if (!to || (typeof to === 'string' && !to.trim())) {
    return 'Recipient "to" is required';
  }
  if (!subject || !String(subject).trim()) {
    return 'Subject is required';
  }
  if ((body == null || body === '') && (html == null || html === '')) {
    return 'Either "body" (plain text) or "html" is required';
  }
  if (attachments != null && !Array.isArray(attachments)) {
    return '"attachments" must be an array when provided';
  }
  return null;
}

/**
 * @param {{ to: string; subject: string; body?: string; html?: string; from?: string; cc?: string|string[]; bcc?: string|string[]; attachments?: Array<{ filename: string; contentBase64: string; mimeType?: string }> }} options
 * @returns {Promise<{ success: boolean; messageId?: string; error?: string; response?: string }>}
 */
export async function sendEmail(options) {
  const validationError = validateSendOptions(options);
  if (validationError) {
    console.warn('[emailService] validation:', validationError, { to: options?.to, subject: options?.subject });
    return { success: false, error: validationError };
  }

  const endpoint = getEmailEndpoint();
  const payload = {
    to: typeof options.to === 'string' ? options.to.trim() : options.to,
    subject: String(options.subject).trim(),
    body: options.body != null ? String(options.body) : undefined,
    html: options.html != null ? String(options.html) : undefined,
    from: options.from,
    cc: options.cc,
    bcc: options.bcc,
    attachments: Array.isArray(options.attachments) ? options.attachments : undefined,
  };

  console.info('[emailService] POST', endpoint, {
    to: payload.to,
    subject: payload.subject,
    hasHtml: !!payload.html,
    hasBody: !!payload.body,
    attachments: payload.attachments?.length || 0,
  });

  const post = (url) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

  try {
    let res = await post(endpoint);
    // Dev: if proxy path fails (e.g. proxy hiccup), try direct email-server once
    if (!res.ok && endpoint.startsWith('/') && process.env.NODE_ENV === 'development') {
      const retryUrl = LOCAL_DIRECT;
      console.warn('[emailService] retrying via', retryUrl, res.status);
      res = await post(retryUrl);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = data.error || data.message || `HTTP ${res.status}`;
      console.warn('[emailService] failed:', err, data);
      return { success: false, error: err, response: data };
    }
    console.info('[emailService] success', { messageId: data.messageId });
    return {
      success: true,
      messageId: data.messageId,
      response: data.response,
    };
  } catch (e) {
    // Network failure on relative URL → try direct in dev (email-server on same machine)
    if (endpoint.startsWith('/') && process.env.NODE_ENV === 'development') {
      try {
        console.warn('[emailService] network error on proxy, retry direct:', e?.message);
        const res = await post(LOCAL_DIRECT);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          return { success: false, error: data.error || data.message || `HTTP ${res.status}`, response: data };
        }
        return { success: true, messageId: data.messageId, response: data.response };
      } catch (e2) {
        console.error('[emailService] direct retry failed:', e2?.message || e2);
        return { success: false, error: e2?.message || String(e2) };
      }
    }
    console.error('[emailService] network error:', e?.message || e);
    return { success: false, error: e?.message || String(e) };
  }
}

export async function sendOrderConfirmation({ to, orderId, orderSummary, customerName }) {
  const name = customerName || 'Customer';
  const body = `Hi ${name},\n\nThank you for your order.\n\nOrder: ${orderId}\n${orderSummary || ''}\n\n— ECDT Portal`;
  const html = `<p>Hi ${escapeHtml(name)},</p><p>Thank you for your order.</p><p><strong>Order:</strong> ${escapeHtml(String(orderId))}</p><pre style="font-family:sans-serif">${escapeHtml(orderSummary || '')}</pre><p>— ECDT Portal</p>`;
  return sendEmail({ to, subject: `Order confirmation — ${orderId}`, body, html });
}

export async function sendShippingNotification({ to, trackingNumber, status, carrier }) {
  const body = `Your shipment status: ${status}\nTracking: ${trackingNumber || '—'}\nCarrier: ${carrier || '—'}`;
  const html = `<p><strong>Shipment update</strong></p><p>Status: ${escapeHtml(status)}</p><p>Tracking: ${escapeHtml(trackingNumber || '—')}</p><p>Carrier: ${escapeHtml(carrier || '—')}</p>`;
  return sendEmail({ to, subject: `Shipping update — ${status}`, body, html });
}

export async function sendContactFormNotification({ to, fromName, fromEmail, message }) {
  const body = `Contact form submission\nFrom: ${fromName} <${fromEmail}>\n\n${message}`;
  const html = `<p><strong>Contact form</strong></p><p>From: ${escapeHtml(fromName)} &lt;${escapeHtml(fromEmail)}&gt;</p><p>${escapeHtml(message).replace(/\n/g, '<br/>')}</p>`;
  return sendEmail({ to, subject: `Contact form: ${fromName || fromEmail}`, body, html });
}

/**
 * HR “add team member” invite: temporary password + role (plain + HTML).
 * Returns same shape as sendEmail — check success; do not block user creation on failure.
 */
export async function sendHrUserInviteEmail({
  to,
  fullName,
  role,
  email,
  temporaryPassword,
  appUrl,
}) {
  const name = (fullName || '').trim() || 'there';
  const url = (appUrl || (typeof window !== 'undefined' ? window.location.origin : '') || '').trim();
  const roleLabel = (role || 'team member').trim();
  const loginEmail = (email || to || '').trim();
  const subject = "You've been added to ECDT Portal";
  const body = [
    `Hi ${name},`,
    '',
    `You have been added to ECDT Portal as ${roleLabel}.`,
    '',
    `Sign in: ${url || '(ask your admin for the portal URL)'}`,
    `Email: ${loginEmail}`,
    `Temporary password: ${temporaryPassword}`,
    '',
    'Please sign in and change your password from your account settings.',
    '',
    '— ECDT Portal (HR)',
  ].join('\n');

  const html = `<div style="max-width:560px;margin:0 auto;font-family:system-ui,-apple-system,sans-serif;font-size:15px;color:#0f172a">
    <p style="font-size:18px;margin:0 0 8px">Welcome, ${escapeHtml(name)}</p>
    <p>You have been added to <strong>ECDT Portal</strong> as <strong style="color:#4f46e5">${escapeHtml(roleLabel)}</strong>.</p>
    <table style="border-collapse:collapse;margin:20px 0;font-size:14px;width:100%">
      <tr><td style="padding:8px 0;color:#64748b;width:140px">Portal</td><td><a href="${escapeHtml(url)}">${escapeHtml(url || 'Open portal')}</a></td></tr>
      <tr><td style="padding:8px 0;color:#64748b">Email</td><td><strong>${escapeHtml(loginEmail)}</strong></td></tr>
      <tr><td style="padding:8px 0;color:#64748b;vertical-align:top">Temporary password</td><td><code style="background:#f1f5f9;padding:4px 8px;border-radius:6px;font-size:13px">${escapeHtml(String(temporaryPassword))}</code></td></tr>
    </table>
    <p style="color:#64748b;font-size:13px">Please sign in and change your password from your account settings.</p>
    <p style="margin-top:24px;color:#94a3b8;font-size:12px">— ECDT Portal (HR)</p>
  </div>`;

  return sendEmail({ to: loginEmail, subject, body, html });
}

/**
 * Notify a user they were assigned as department manager (on department create).
 * Fire-and-forget from UI; failures are logged only.
 */
export async function sendDepartmentManagerAssignedEmail({
  to,
  managerName,
  departmentName,
  departmentCode,
  portalUrl,
}) {
  const name = (managerName || '').trim() || 'there';
  const dept = (departmentName || '').trim() || 'a department';
  const code = (departmentCode || '').trim();
  const portal = (portalUrl || getPortalUrlForEmails()).replace(/\/$/, '');
  const codeLine = code ? `Code: ${code}\n` : '';
  const subject = `You are assigned as manager — ${dept}`;
  const body = `Hi ${name},

You have been assigned as the manager for the following department in ECDT Portal:

Department: ${dept}
${codeLine}
Open the portal: ${portal}

— ECDT Portal`;
  const codeHtml = code
    ? `<tr><td style="padding:6px 12px 6px 0;color:#64748b">Code</td><td><strong>${escapeHtml(code)}</strong></td></tr>`
    : '';
  const html = `<div style="max-width:560px;margin:0 auto;font-family:system-ui,-apple-system,sans-serif;font-size:15px;color:#0f172a">
    <p style="font-size:18px;margin:0 0 8px">Hi ${escapeHtml(name)},</p>
    <p>You have been assigned as <strong>manager</strong> for this department:</p>
    <table style="border-collapse:collapse;margin:16px 0;font-size:14px">
      <tr><td style="padding:6px 12px 6px 0;color:#64748b">Department</td><td><strong>${escapeHtml(dept)}</strong></td></tr>
      ${codeHtml}
    </table>
    <p><a href="${escapeHtml(portal)}" style="color:#4f46e5">Open ECDT Portal</a></p>
    <p style="margin-top:20px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  return sendEmail({ to, subject, body, html });
}

export async function sendWelcomeEmail({ to, displayName, appUrl }) {
  const name = displayName || 'there';
  const url = appUrl || getPortalUrlForEmails();
  const body = `Welcome, ${name}!\n\nSign in: ${url}`;
  const html = `<p>Welcome, ${escapeHtml(name)}!</p><p><a href="${escapeHtml(url)}">Sign in</a></p>`;
  return sendEmail({ to, subject: 'Welcome to ECDT Portal', body, html });
}

/** Opt-in: set REACT_APP_NOTIFY_LOGIN_EMAILS=true to send a sign-in alert (avoids noise by default). */
export function shouldSendLoginNotificationEmail() {
  return String(process.env.REACT_APP_NOTIFY_LOGIN_EMAILS || '').toLowerCase() === 'true';
}

export async function sendLoginNotificationEmail({ to, displayName }) {
  const name = (displayName || '').trim() || 'there';
  const portal = getPortalUrlForEmails();
  const when = new Date().toISOString();
  const subject = 'New sign-in to ECDT Portal';
  const body = `Hi ${name},

Your ECDT Portal account was just used to sign in.

Time (UTC): ${when}
Portal: ${portal}

If this wasn’t you, change your password immediately from the portal or use “Forgot password” on the sign-in page.

— ECDT Portal`;
  const html = `<div style="max-width:560px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p>Hi ${escapeHtml(name)},</p>
    <p>Your <strong>ECDT Portal</strong> account was just used to sign in.</p>
    <p style="color:#64748b;font-size:13px">Time (UTC): ${escapeHtml(when)}<br/>Portal: <a href="${escapeHtml(portal)}">${escapeHtml(portal)}</a></p>
    <p>If this wasn’t you, change your password immediately or use <strong>Forgot password</strong> on the sign-in page.</p>
    <p style="margin-top:20px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  return sendEmail({ to, subject, body, html });
}

export async function sendPasswordChangedEmail({ to, displayName }) {
  const name = (displayName || '').trim() || 'there';
  const portal = getPortalUrlForEmails();
  const when = new Date().toISOString();
  const subject = 'Your ECDT Portal password was changed';
  const body = `Hi ${name},

Your password was changed successfully.

Time (UTC): ${when}
Portal: ${portal}

If you did not make this change, contact your administrator immediately.

— ECDT Portal`;
  const html = `<div style="max-width:560px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p>Hi ${escapeHtml(name)},</p>
    <p>Your <strong>ECDT Portal</strong> password was changed successfully.</p>
    <p style="color:#64748b;font-size:13px">Time (UTC): ${escapeHtml(when)}</p>
    <p>If you did not make this change, contact your administrator immediately.</p>
    <p style="margin-top:16px"><a href="${escapeHtml(portal)}">Open portal</a></p>
    <p style="margin-top:20px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  return sendEmail({ to, subject, body, html });
}

/**
 * Leave application submitted — confirmation to employee. Optional HR copy via REACT_APP_LEAVE_NOTIFY_EMAIL.
 */
export async function sendLeaveRequestSubmittedEmail({
  to,
  employeeName,
  leaveTypeLabel,
  startDate,
  endDate,
  reason,
}) {
  const name = (employeeName || '').trim() || 'there';
  const portal = getPortalUrlForEmails();
  const subject = `Leave request submitted — ${leaveTypeLabel || 'Leave'}`;
  const body = `Hi ${name},

Your leave request has been submitted and is pending approval.

Type: ${leaveTypeLabel || '—'}
From: ${startDate || '—'} to ${endDate || '—'}
${reason ? `Notes: ${reason}\n` : ''}
View status in the portal: ${portal}/hr/leave-request

— ECDT Portal`;
  const html = `<div style="max-width:560px;font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">
    <p>Hi ${escapeHtml(name)},</p>
    <p>Your <strong>leave request</strong> has been submitted and is <strong>pending approval</strong>.</p>
    <table style="border-collapse:collapse;margin:16px 0;font-size:14px">
      <tr><td style="padding:6px 12px 6px 0;color:#64748b">Type</td><td><strong>${escapeHtml(leaveTypeLabel || '—')}</strong></td></tr>
      <tr><td style="padding:6px 12px 6px 0;color:#64748b">Dates</td><td>${escapeHtml(startDate || '—')} → ${escapeHtml(endDate || '—')}</td></tr>
    </table>
    ${reason ? `<p style="color:#64748b"><strong>Notes:</strong> ${escapeHtml(reason).replace(/\n/g, '<br/>')}</p>` : ''}
    <p><a href="${escapeHtml(portal)}/hr/leave-request">View leave requests in the portal</a></p>
    <p style="margin-top:20px;color:#94a3b8;font-size:12px">— ECDT Portal</p>
  </div>`;
  const results = [await sendEmail({ to, subject, body, html })];
  const hrTo = (process.env.REACT_APP_LEAVE_NOTIFY_EMAIL || '').trim();
  if (hrTo) {
    const hrSubject = `[Leave] ${name} — ${leaveTypeLabel || 'request'}`;
    const hrBody = `Leave request submitted\nEmployee: ${name} (${to})\nType: ${leaveTypeLabel}\nDates: ${startDate} → ${endDate}\n${reason ? `Notes: ${reason}\n` : ''}`;
    const hrHtml = `<p><strong>Leave request</strong></p><p>Employee: ${escapeHtml(name)} &lt;${escapeHtml(to)}&gt;</p><p>Type: ${escapeHtml(leaveTypeLabel || '—')}</p><p>Dates: ${escapeHtml(startDate || '—')} → ${escapeHtml(endDate || '—')}</p>${reason ? `<p>${escapeHtml(reason).replace(/\n/g, '<br/>')}</p>` : ''}`;
    results.push(await sendEmail({ to: hrTo, subject: hrSubject, body: hrBody, html: hrHtml }));
  }
  const failed = results.filter((r) => !r.success);
  return failed.length ? { success: false, error: failed.map((f) => f.error).join('; ') } : { success: true };
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
